import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as rawApi from "../services/api";
import { addObject, addList, addPathId } from "../data/actions.js";
import {
  addActiveApiCall,
  removeActiveApiCall,
} from "../services/apicallsprogress/activeapicalls/actions.js";
import {
  addActiveConnect,
  removeActiveConnect,
} from "../services/apicallsprogress/activeconnects/actions";
import { addSchema } from "../services/schema/actions.js";
import { isEqual } from "lodash";

import { createRegex } from "../tiny-functions/createRegex";
import * as propertyTypes from "../propertyTypes";

import store from "../../store";

import getOname from "../tiny-functions/getOname";

import { useApi } from "../../rootWrapper";
import getSchemaName from "../tiny-functions/getSchemaName";
import useInterval from "./useInterval";

function isEmpty(obj) {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
}

const sleep = async (ms) =>
  new Promise((resolve) => setTimeout(() => resolve(), ms));

const usePrevious = (value, initialValue) => {
  const ref = useRef(initialValue);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const useEffectDebugger = (effectHook, dependencies, dependencyNames = []) => {
  const previousDeps = usePrevious(dependencies, []);

  const changedDeps = dependencies.reduce((accum, dependency, index) => {
    if (dependency !== previousDeps[index]) {
      const keyName = dependencyNames[index] || index;
      return {
        ...accum,
        [keyName]: {
          before: previousDeps[index],
          after: dependency,
        },
      };
    }

    return accum;
  }, {});

  if (Object.keys(changedDeps).length) {
    console.log("[use-effect-debugger] ", changedDeps);
  }

  useEffect(effectHook, dependencies);
};

export function useRawApi() {
  return rawApi;
}

export function useSchema(path) {
  const name = getSchemaName(path);
  const storeSchema = useSelector((state) => state.services.schema[name]);
  const [schema, setSchema] = useState(storeSchema);
  const api = useApi();

  useEffect(() => {
    if (!path) {
      return;
    }

    if (storeSchema == null) {
      const schemaPath = path
        .split("/")
        .map((l1) => [l1.split(".")[0], 0].join("."))
        .join("/");

      api.get(schemaPath).then((schemaRes) => setSchema(schemaRes.schema));
    } else {
      if (!isEqual(storeSchema, schema)) {
        setSchema(storeSchema);
      }
    }
  }, [storeSchema]);

  if (!path) {
    return;
  }

  return schema;
}

export function useSchemaForce(path) {
  const name = getSchemaName(path);
  const storeSchema = useSelector((state) => state.services.schema[name]);
  const [schema, setSchema] = useState(storeSchema);
  const api = useApi();

  useEffect(() => {
    if (storeSchema != null && !isEqual(storeSchema, schema)) {
      setSchema(storeSchema);
    }
  }, [storeSchema]);

  function refreshSchema() {
    api
      .get(path, { schema: "t" })
      .then((schemaRes) => setSchema(schemaRes.schema));
  }

  useEffect(() => {
    if (!path) {
      return;
    }
    refreshSchema();
  }, [path]);

  if (!path) {
    return;
  }

  return [schema, refreshSchema];
}

// export function useSchema(path, force) {
//   const [willCallApi, setWillCallApi] = useState(false);
//   const storeSchema = useSelector(state => state.services.schema);
//   const name = getOname(path);

//   useEffect(() => {
//     if (willCallApi) {
//       async function asyncEffect() {
//         const schemaPath = path
//           .split("/")
//           .map(l1 => [l1.split(".")[0], 0].join("."))
//           .join("/");
//         try {
//           await apiCall(schemaPath);
//         } catch (e) {
//           console.log("Failed to get schema ", e);
//         }
//       }
//       asyncEffect();
//     }
//   }, [path, willCallApi]);

//   if (!path) {
//     return null;
//   }

//   let schema = null;
//   if (force !== "FORCE" && storeSchema[name]) {
//     schema = storeSchema[name];
//   } else {
//     if (!willCallApi) {
//       setWillCallApi(true);
//     }
//   }
//   return schema;
// }

export async function apiCall(path, tries = 0) {
  if (!path) {
    return null;
  }
  const apiRes = await rawApi.get(path);
  if (apiRes == null) {
    if (false && tries < 20) {
      await sleep(500);
      return await apiCall(path, tries + 1);
    }
    return new Error("apiCall timed out");
  }
  if ("schema" in apiRes) {
    store.dispatch(addSchema(apiRes, path));
  }
  if ("zeroRows" in apiRes) {
    store.dispatch(addPathId(path, []));
  } else if ("object" in apiRes) {
    const name = apiRes.name;
    const id = apiRes.object[apiRes.pk];
    store.dispatch(addObject(name, apiRes.object, id, apiRes.ts));
    store.dispatch(addPathId(path, id));
  } else if ("list" in apiRes) {
    const name = apiRes.name;
    const ids = [];
    for (const object of apiRes.list) {
      const id = object[apiRes.pk];
      ids.push(id);
    }

    store.dispatch(addList(name, apiRes.list, apiRes.pk, apiRes.ts));
    store.dispatch(addPathId(path, ids));
  }

  return apiRes;
}

export function useId(path) {
  const ids = useSelector((state) => state.data[`idFor${path}`]);
  return ids;
}

export function useGet(path) {
  const [loading, setLoading] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);
  const newstOperator = useSelector((state) => state.services.ids.operator);
  const [operator, setOperator] = useState(newstOperator);
  const [error, setError] = useState(null);

  const refresh = useCallback(() => {
    setRefreshCount(refreshCount + 1);
  }, [refreshCount]);

  //This calls one time. Could call several times too!!!!
  useEffect(() => {
    setLoading(true);
    const asyncEffect = async () => {
      await apiCall(path);
      setLoading(false);
    };
    asyncEffect();
  }, [path, refreshCount]);

  useEffect(() => {
    if (operator !== newstOperator) {
      setOperator(newstOperator);
      refresh();
    }
  }, [newstOperator, operator, refresh]);

  return [loading, refresh, error];
}

export function useData(path) {
  const name = getOname(path);
  const storeData = useSelector((state) => state.data[name]);
  const ids = useId(path);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (Array.isArray(ids)) {
      const newDataList =
        ids
          .filter((id) => storeData?.[id]?.props != null)
          .map((id) => storeData[id].props) || [];

      let wantUpdate = false;
      for (const i of newDataList.keys()) {
        if (!isEqual(newDataList[i], data?.[i])) {
          wantUpdate = true;
          break;
        }
      }

      if (wantUpdate) {
        setData(newDataList);
      }
    } else {
      if (
        storeData?.[ids]?.props != null &&
        !isEqual(storeData[ids].props, data)
      ) {
        setData(storeData[ids].props);
      }
    }
  }, [ids, storeData, data]);

  return data;
}

export function useGetData(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);
  const [hardRefreshCount, setHardRefreshCount] = useState(0);
  const api = useApi();
  const dispatch = useDispatch();

  const refresh = () => setRefreshCount((prev) => prev + 1);
  const hardRefresh = () => setHardRefreshCount((prev) => prev + 1);

  //This calls one time. Could call several times too!!!!
  useEffect(() => {
    setLoading(true);

    const asyncEffect = async () => {
      if (!store.getState().services.api.JRR) {
        // if (!api?.get) { //
        await sleep(1000);
        refresh();
        return;
      }

      dispatch(addActiveApiCall(path));
      let apiRes;
      if (
        path != null &&
        path.length > 0 &&
        path.trim() !== "&" &&
        typeof api?.get === "function"
      ) {
        apiRes = await api?.get(path);
      }

      if (apiRes?.zeroRows) {
        setData([]);
      } else if (apiRes?.object) {
        setData(apiRes.object);
      } else if (apiRes?.list) {
        setData(apiRes.list);
      }
      setLoading(false);
      dispatch(removeActiveApiCall(path));
    };
    asyncEffect();
  }, [path, refreshCount]);

  useEffect(() => {
    //This is simply a copy of refresh function but calls it with a special GET parameter "hardRefresh"
    setLoading(true);

    const asyncEffect = async () => {
      if (hardRefreshCount < 1) {
        return;
      }

      if (!store.getState().services.api.JRR) {
        // if (!api?.get) { //
        await sleep(1000);
        refresh();
        return;
      }

      let apiRes;
      if (
        path != null &&
        path.length > 0 &&
        path.trim() !== "&" &&
        typeof api?.get === "function"
      ) {
        apiRes = await api?.get(path, {
          httpGetParameters: {
            hardRefresh: true,
          },
        });
      }

      if (apiRes?.zeroRows) {
        setData([]);
      } else if (apiRes?.object) {
        setData(apiRes.object);
      } else if (apiRes?.list) {
        setData(apiRes.list);
      }
      setLoading(false);
    };
    asyncEffect();
  }, [path, hardRefreshCount]);

  return [data, loading, refresh, hardRefresh];
}

export function usePath(path, idsources) {
  const sessionids = store.getState().services.ids;
  const ids = {
    ...sessionids,
    ...idsources,
  };
  const newPath = [];

  for (let section of path.split("/")) {
    const [name, id] = section.split(".");
    if (id != null) {
      if (!Number.isNaN(Number(id))) {
        //This means that id is a string that contains a number i.e. "15"
        newPath.push(`${name}.${id}`);
      } else if (name in ids) {
        newPath.push(`${name}.${ids[name]}`);
      } else {
        // console.error(`[${name}] is missing on ids for ${path}`);
        return "";
      }
    } else {
      newPath.push(name);
    }
  }
  return newPath.join("/");
}

function rewritePath(path, trailingId) {
  const newPath = path.split("/");
  const lastIndex = newPath.length - 1;
  const [name] = newPath[lastIndex].split(".");

  if (trailingId == null) {
    newPath[lastIndex] = name;
  } else {
    newPath[lastIndex] = `${name}.${trailingId}`;
  }

  return newPath.join("/");
}

function isValueNull(value) {
  if (value == null) {
    return true;
  }

  if (value === "_NULL") {
    return true;
  }

  return false;
}

export function useForm(
  dataPath,
  editRow = null,
  supersedeSchema = null,
  options = {} /*  */,
  mode,
  suffixForSS = ""
) {
  const [input, setInput] = useState({});
  const [touched, setTouched] = useState(false);
  const [invalid, setInvalid] = useState({});
  const bigSchema = useSchema(dataPath);
  const [schemaBase, setSchemaBase] = useState({});
  const [schema, setSchema] = useState({});

  // console.log("RENDER");
  // console.log(invalid);
  // console.log(input);
  // for (const propName in schema) {
  //   console.log(propName, isRequired(propName));
  // }
  useEffect(() => {
    //This is to fix a problem where after reset form schemabase was stil loading from old path, poisoning the input for subsequent forms
    setSchemaBase({ ...bigSchema, dataPath });
  }, [bigSchema]);

  useEffect(() => {
    let schemaBaseProps = {};
    if (
      schemaBase?.dataPath &&
      schemaBase?.props &&
      schemaBase.dataPath !== "" &&
      dataPath !== "" &&
      getSchemaName(schemaBase.dataPath) === getSchemaName(dataPath)
    ) {
      schemaBaseProps = schemaBase.props;
    }

    const newSchema = { ...schemaBaseProps };

    //This suports a "local level schema" (supersedeSchema)
    //So the front-end can do small changes without the need of changing the back-end
    //i.e. Want a custom label for this form, but don't want to change back-end BaseProperty
    //because it will affect other objects/forms
    if (supersedeSchema) {
      for (const propName in supersedeSchema) {
        if (
          schemaBaseProps &&
          !isEmpty(schemaBaseProps) &&
          propName in schemaBaseProps
        ) {
          newSchema[propName] = {
            ...schemaBaseProps[propName],
            ...supersedeSchema[propName],
          };
        } else {
          newSchema[propName] = { ...supersedeSchema[propName] };
          // if (dataPath !== "" && dataPath != null) {
          //   console.error(
          //     "customSchema has wrong prop: [" +
          //       propName +
          //       "]. Probably misstyped propName?"
          //   );
          // }
        }
      }
    }

    //Hack to avoid loops
    if (JSON.stringify(schema) !== JSON.stringify(newSchema)) {
      setSchema(newSchema);
    }

    if (editRow != null) {
      if (isEmpty(editRow) || mode === "insert") {
        //Insert Mode
        const sessionStorageData =
          JSON.parse(sessionStorage.getItem(dataPath + suffixForSS)) || {};
        //Setting default values for input
        for (const propName in newSchema) {
          //Commenting and changing to default because defaultValue is simply wrong?
          // if ("defaultValue" in schema[propName]) {
          //   changeInput(propName, schema[propName].defaultValue);
          // }
          if (editRow[propName] != null) {
            changeInput(propName, editRow[propName]);
          } else if (sessionStorageData?.[propName] != null) {
            changeInput(propName, sessionStorageData?.[propName]);
          } else if ("default" in newSchema[propName] && !(propName in input)) {
            changeInput(propName, newSchema[propName].default);
          }
        }
      } else {
        //Edit Mode

        for (const propName in editRow) {
          /*
          if (schema[propName].type === propertyTypes.PRIMARY_KEY) {
            continue
          }
          */
          if (editRow[propName] == null) {
            changeInput(propName, undefined);
          } else {
            changeInput(propName, editRow[propName]);
          }
        }
      }
    }
  }, [schemaBase, supersedeSchema, editRow, suffixForSS]);

  const validateField = useCallback(
    (value, propName, type) => {
      if (!type) {
        return true;
      }
      if (
        (value == null || value === "_NULL" || value === "") &&
        !isRequired(propName)
      ) {
        return true;
      }
      if (type === propertyTypes.CUSTOM) {
        return true;
      } else if (type === propertyTypes.OPEN_INPUT) {
        if (value === "" && !isRequired(propName)) {
          return true;
        }
        return createRegex(schema[propName].regex).test(value);
      } else if (type === propertyTypes.SELECT_OPTION) {
        if (value === "" && !isRequired(propName)) {
          return true;
        }
        for (let option of schema[propName].dropdownOptions) {
          if (Array.isArray(option)) {
            if (isValueNull(value) && isValueNull(option[0])) {
              return true;
            }
            if (String(value) === String(option[0])) {
              return true;
            }
          } else {
            if (String(value) === String(option)) {
              return true;
            }
          }
        }
        return false;
      } else if (type === propertyTypes.BOOLEAN) {
        if (value === true || value === false) {
          return true;
        } else {
          if (!isRequired(propName)) {
            return true;
          }
          return false;
        }
      } else if (type === propertyTypes.PRIMARY_KEY) {
        return true;
      } else {
        throw new Error(
          "Unknown type [" + type + "] on validateField for prop: " + propName
        );
      }
    },
    [schema]
  );

  useEffect(() => {
    for (const propName in input) {
      if (
        validateField(
          input[propName],
          propName,
          schema[propName]?.expectedType === "custom"
            ? "cu"
            : schema[propName]?.type
        )
      ) {
        //tastless hack for custom to work now. Ideally change type on backend and fix all core code
        setInvalid((prevInvalid) => ({ ...prevInvalid, [propName]: false }));
      } else {
        setInvalid((prevInvalid) => ({ ...prevInvalid, [propName]: true }));
      }
    }
  }, [input, validateField, setInvalid]);

  function isRequired(propName) {
    let required = schema?.[propName]?.required || false;

    rulesLoop: for (const rule of schema?.[propName]?.requiredRules || []) {
      //First rule matched will stop loop
      if (rule.length != 4) {
        throw new Error("requiredRules wrong setting " + rule);
      }
      const [condition, conditionProp, operator, values] = rule;

      const trigger = () => {
        switch (condition) {
          case "optionalif":
            required = false;
            break;
          case "requiredif":
            required = true;
            break;
          default:
            throw new Error(`Unkown rule condition ${condition}`);
        }
      };

      switch (operator) {
        case "notEmpty":
          if (input.includes(conditionProp)) {
            trigger();
            break rulesLoop;
          }
          break;
        case "equal":
        case "equals":
          if (input[conditionProp] && values.includes(input[conditionProp])) {
            trigger();
            break rulesLoop;
          }
          break;
        default:
          throw new Error(`Unkown rule operator ${operator}`);
      }
    }

    return required;
  }

  function castInput(stringInput) {
    const castedInput = {};
    for (const propName in stringInput) {
      switch (schema?.[propName]?.expectedType) {
        case "string":
          castedInput[propName] = String(stringInput[propName]);
          break;
        case "boolean":
          castedInput[propName] = Boolean(stringInput[propName]);
          break;
        case "integer":
        case "float":
          castedInput[propName] = Number(stringInput[propName]);
          break;
        default:
          castedInput[propName] = stringInput[propName];
      }
    }
    return castedInput;
  }

  function summarize() {
    if (mode === "insert" || editRow == null || isEmpty(editRow)) {
      //NEED TO DO FINAL INPUT HERE AND SEND _NULL IF input is null and has been touched
      return [rewritePath(dataPath, null), castInput(input)];
    }
    //Comparing input to initial editRows. If values haven't change, there's no
    //need to submit them to the API!
    const finalInput = {};
    let id = null;
    for (const propName in schema) {
      if (schema[propName].type === propertyTypes.PRIMARY_KEY) {
        id = editRow[propName];
      } else {
        if (editRow[propName] == null && input[propName] === undefined) {
          //This is ok. It means it didn't change because it's null on redux
          //but useForm translated null to undefined at the entry
          continue;
        }
        if (input[propName] == null) {
          finalInput[propName] = "_NULL";
        } else if (editRow[propName] !== input[propName]) {
          finalInput[propName] = input[propName];
        }
      }
    }

    if (id == null && dataPath) {
      throw new Error(
        "useForm.summarize found no primary key on editRow on edit mode"
      );
    }

    return [rewritePath(dataPath, id), castInput(finalInput)];
  }

  function resetForm() {
    setInput({});
    setInvalid({});
    setTouched(false);
  }

  const fakeInput = { ...input };
  for (const propName in schema) {
    if (!(propName in input) || input[propName] == null) {
      fakeInput[propName] = "";
    }
  }

  const form = {
    input: fakeInput,
    realInput: input,
    changeInput: exposedChangeInput,
    onBlurSessionStorage,
    invalid,
    summarize,
    bigSchema,
    schema,
    resetForm,
    touched,
    isRequired,
    mode,
  };

  function changeInput(propName, newValue) {
    if (newValue === input[propName]) {
      return;
    }

    if (options.onChangeInput) {
      options.onChangeInput(propName, newValue, form);
    }

    setInput((prevInput) => {
      return { ...prevInput, [propName]: newValue };
    });
  }

  function onBlurSessionStorage() {
    if (mode === "edit" || dataPath === "" || dataPath == null) {
      return;
    }
    sessionStorage.setItem(dataPath + suffixForSS, JSON.stringify(input));
  }

  function exposedChangeInput(propName, newValue) {
    changeInput(propName, newValue);
    if (!touched) {
      setTouched(true);
    }
  }

  return form;
}

export function useSubscribe(
  basePath,
  pathids = {},
  additionalHttpGetParameters = "",
  activeSubscription = true
) {
  const path = usePath(basePath, { ...pathids });
  const url = path + additionalHttpGetParameters;
  const [loading, refresh] = useGet(url);
  const data = useData(url);

  // useEffect(() => {
  //   if (!activeSubscription) {
  //     return;
  //   }
  //   store.dispatch(addActiveSubscription(url));
  //   return () => store.dispatch(removeActiveSubscription(url));
  // }, [url]);

  return {
    data,
    path,
    loading,
    refresh,
  };
}

export function useConnect(
  basePath,
  pathids = {},
  additionalHttpGetParameters = "",
  refreshInterval
) {
  const path = usePath(basePath, { ...pathids });
  const url = path + additionalHttpGetParameters;
  const [data, loading, refresh, hardRefresh] = useGetData(url);
  const dispatch = useDispatch();

  useInterval(() => {
    if (refreshInterval && typeof refresh === "function") refresh();
  }, [refreshInterval || 10000]);

  useEffect(() => {
    dispatch(addActiveConnect(url));
    return () => dispatch(removeActiveConnect(url));
  }, [url]);

  useEffect(() => {
    const onRefresh = () => {
      refresh();
    };
    const onHardRefresh = () => {
      hardRefresh();
    };
    window.addEventListener("refreshAllConnects", onRefresh);
    window.addEventListener(`refresh${path}`, onRefresh);
    window.addEventListener(`hardRefresh${path}`, onHardRefresh);
    return () => {
      window.removeEventListener("refreshAllConnects", onRefresh);
      window.removeEventListener(`refresh${path}`, onRefresh);
      window.removeEventListener(`hardRefresh${path}`, onHardRefresh);
    };
  }, [path]);

  return {
    data,
    path,
    loading,
    refresh,
    hardRefresh,
  };
}

export function useDropdownOptionsFromCall(optionsCallback, path) {
  const { data } = useConnect(path);
  const [options, setOptions] = useState(null);

  useEffect(() => {
    if (data) {
      const newOptions = [];
      for (const row of data) {
        newOptions.push(optionsCallback(row));
      }
      setOptions(newOptions);
    }
  }, [data]);

  return options;
}

export default {};
